import {
  useAssignFeatureToAccounts,
  useAssignFeatureToWorkspaces,
  useFeatureAssignments,
} from '@gripp/shared-logic';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AssignmentModal } from '../../shared/components/assignment/assignmentModal';
import { FeatureAccountAssignmentList } from './featureAccountAssignmentList';
import { FeatureWorkspaceAssignmentList } from './featureWorkspaceAssignmentList';

interface Props {
  featureId: string;
  title: string;
  onComplete: () => void;
  initialTab?: 'workspace' | 'account';
}

export const FeatureAssignmentForm = ({
  featureId,
  title,
  onComplete,
  initialTab,
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data, isPending } = useFeatureAssignments(featureId);
  const found = data?.feature?.findFeatureAssignments;

  const { mutateAsync: assignToWorkspaces } = useAssignFeatureToWorkspaces();
  const { mutateAsync: assignToAccounts } = useAssignFeatureToAccounts();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['feature'] });
    queryClient.invalidateQueries({
      queryKey: ['feature', 'assignments', featureId],
    });
  };

  return (
    <AssignmentModal
      title={title}
      isLoading={isPending}
      initialTabKey={initialTab}
      onComplete={onComplete}
      tabs={[
        {
          key: 'workspace',
          label: t('features.assignment.workspaces'),
          searchPlaceholder: t(
            'features.assignment.workspaceSearchPlaceholder'
          ),
          assignments: found?.workspaces,
          onSave: async ({ selected }) => {
            await assignToWorkspaces({
              input: { featureId, workspaceIds: selected },
            } as never);
            invalidate();
          },
          renderList: (p) => (
            <FeatureWorkspaceAssignmentList
              featureId={featureId}
              maxHeight={p.maxHeight}
              keyword={p.keyword}
              workspaceAssignments={p.assignments}
              onWorkspaceAssignmentsChange={p.onAssignmentsChange}
            />
          ),
        },
        {
          key: 'account',
          label: t('features.assignment.accounts'),
          searchPlaceholder: t('features.assignment.accountSearchPlaceholder'),
          assignments: found?.accounts,
          onSave: async ({ selected }) => {
            await assignToAccounts({
              input: { featureId, accountIds: selected },
            } as never);
            invalidate();
          },
          renderList: (p) => (
            <FeatureAccountAssignmentList
              featureId={featureId}
              maxHeight={p.maxHeight}
              keyword={p.keyword}
              accountAssignments={p.assignments}
              onAccountAssignmentsChange={p.onAssignmentsChange}
            />
          ),
        },
      ]}
    />
  );
};
